import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {
    Data,
    IFinalEvaluatedArea,
    IStickyDeadSymInfo,
    IStickyScatter,
    IXHoleCordInfo,
    Mental2GameData,
    Mental2InitData
} from "./Data";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/utils/server/IData";
import {Game} from "../Game";
import {IBigWinLevel, IWinRatioConfig} from "../interfaces/Interface";
import {Config} from "../config/Config";
import {GameFeatureMode, SYMBOL} from "../enums/Enum";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";

export class Model extends GameModel<Mental2InitData, Mental2GameData> {
    public isRestore: boolean = false;
    public winRatioConfig: IWinRatioConfig;
    public winRatio: number;
    public fireFrameCount: number;
    public reelWinPos: Map<string, boolean[][][]> = new Map<string, boolean[][][]>();
    public deadSymCount: number = 0;
    public enhancerFeatures: SYMBOL[];
    public s2InReels: number[] = [-1, -1];
    public stickyS2: number[];

    public scatterLandedPos: number[];
    public totalScatterLanded: number;
    public scatterAnticipationReels: boolean[];
    public xHoleMultiplier: number[][] = [];
    public xGodInReels: number[];
    public deadMultiplierPlacedPositions: boolean[][][];
    public deadMultiplierToBePlacedPositionForReels: boolean[][][];
    public deadMultiplierPlacedValues: number[][][];
    public stickyDeadSymInfoCopy: { [symbolName: string]: IStickyDeadSymInfo };
    public xMentalPos: PIXI.Point;
    public previousFSLeftCount: number;

    constructor(api: ApiPlugin, triggerEvents: boolean = true) {
        super(api, triggerEvents);
        api.events.once("featureName", (data: string) => this.onFeatureName(data));
        api.events.on(APIEvent.BET, (betData: APIFeatureBetEventData) => this.onBet(betData));
        Game.apiPlugin.events.on(APIEvent.ACTION_SPINS_BET, (betData: APIFeatureBetEventData) => {
            for (let index = 0; index < Game.model.initData.featureBuyTimesBetValue.length; index++) {
                if (Game.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    Game.buyFeatureName = betData.featureName;
                    break;
                }
            }
        });
    }

    public setPreviousSpinFSLeftCount() {
        this.previousFSLeftCount = this.data.freespinsLeft;
    }

    public isBonus(): boolean {
        return Game.model.data.mode !== GameFeatureMode.NORMAL;
    }

    public getWinAmount(): number {
        return this.isBonus() ? this.gameData.totalSpinWinnings : this.data.accumulatedRoundWin;
    }

    public isSplitPresentInS2Reveal(): boolean {
        return (this.gameData.firstS2FeatureTriggered.indexOf(SYMBOL.X_SPLIT) > -1 || this.gameData.secondS2FeatureTriggered.indexOf(SYMBOL.X_SPLIT) > -1);
    }

    // #355
    public getBigWinLevels(): IBigWinLevel[] {
        let levels: IBigWinLevel[] = [],
            levelsToPickFrom: IBigWinLevel[];
        const ratio: number = this.winRatio,
            isLevel1Pushed: boolean = (!this.gameData.wasFeatureBuy);

        if (isLevel1Pushed) {
            if (Game.buyFeatureName === "BOOSTED_BET_4") {
                levelsToPickFrom = Config.BIG_WIN_RATIOS_XMENTAL_BOOSTED_BET;
            } else {
                levelsToPickFrom = Config.WIN_RATIOS.bigWin;
            }
        } else {
            levelsToPickFrom = Config.BIG_WIN_RATIOS_FEATURE_BUYS;
        }

        const length: number = levelsToPickFrom.length;
        for (let index: number = 0; index < length; index++) {
            if (ratio >= levelsToPickFrom[index].ratio) {
                levels.push(JSON.parse(JSON.stringify(levelsToPickFrom[index])));
            }
        }

        levels = this.setLastConfig(ratio, levels);

        return levels;
    }

    public isXGodMaxWinTriggered(): boolean {
        return this.xGodInReels.indexOf(-1) === -1 && this.gameData.brokeTheBank;
    }

    public parseStickyDeadMultiplier(data: Mental2GameData) {
        this.resetAllMultiplierPlacedPosition(data);
        this.stickyDeadSymInfoCopy = JSON.parse(JSON.stringify(data.stickyDeadSymCounter));
        const landedReelsToBePlacedPositions: boolean[][][] = [];
        data.reels.forEach((reelSymArr: string[], reelId: number) => {
            landedReelsToBePlacedPositions[reelId] = [];
            reelSymArr.forEach((sym: string, rowId: number) => {
                landedReelsToBePlacedPositions[reelId][rowId] = [];
                const isStickyS2: boolean = this.stickyS2[reelId] === rowId;
                const deadSym: IStickyDeadSymInfo = this.stickyDeadSymInfoCopy[sym];
                const subRowLength: number = data.fireReels[reelId] ? 3 : data.fireFrames[reelId][rowId] ? 2 : 1;
                for (let subRowId: number = 0; subRowId < subRowLength; subRowId++) {
                    if (deadSym?.used > 0 && !isStickyS2) {
                        landedReelsToBePlacedPositions[reelId][rowId].push(true);
                        deadSym.used--;
                    } else {
                        landedReelsToBePlacedPositions[reelId][rowId].push(false);
                    }
                }

            });
        });
        this.deadMultiplierToBePlacedPositionForReels = landedReelsToBePlacedPositions;
    }

    public shouldPlayXHoleAnticipation(): boolean {
        return (Game.model.gameData.fireReels[2] || Game.model.gameData.fireFrames[2][1]) && (Game.model.gameData.reels[2].includes(SYMBOL.X_HOLE));
    }

    public shouldPlayXMentalAnticipation(nextReelId: number): boolean {
        let xMentalReelId: number = Game.model.xMentalPos.x;
        const isEligible: boolean = Game.model.gameData.featureBuyName !== "BOOSTED_BET_4" && nextReelId === xMentalReelId;
        if (isEligible) {
            let isAllFireFrameInReel: boolean = Game.model.gameData.fireFrames[xMentalReelId][0] && Game.model.gameData.fireFrames[xMentalReelId][1];
            if (xMentalReelId % 2 === 0) {
                isAllFireFrameInReel = isAllFireFrameInReel && Game.model.gameData.fireFrames[xMentalReelId][2]
            }
            const isAllPosHaveFireFrameInReel: boolean = Game.model.gameData.fireReels[xMentalReelId] || isAllFireFrameInReel;
            const isFireFrameAvailable: boolean = (Game.model.gameData.fireFrames[xMentalReelId][0] || Game.model.gameData.fireFrames[xMentalReelId][1] || Game.model.gameData.fireFrames[xMentalReelId][2]);
            return !isAllPosHaveFireFrameInReel && isFireFrameAvailable;
        } else {
            return false;
        }
    }

    public onGame(data: Mental2GameData): void {
        super.onGame(data);
        this.deadSymCount = 0;
        this.isRestore = false;
        this.winRatio = this.getWinAmount() / data.playedBetValue;
        this.winRatioConfig = this.calculateWinRatioConfig(this.winRatio);
        this.fireFrameCount = this.calculateFireFrame(data);
        this.enhancerFeatures = [data.evaluatedArea[1][2] as SYMBOL, data.evaluatedArea[2][3] as SYMBOL, data.evaluatedArea[3][2] as SYMBOL];
        this.reelWinPos.clear();
        if (data.betWayWins?.length) {
            this.parseBetWaysWin(data);
        }

        this.scatterLandedPos = this.parseScatterLanded(data.reels);
        this.totalScatterLanded = this.parseTotalScatters(this.scatterLandedPos);
        this.scatterAnticipationReels = this.parseScatterAnticipationReels();
        this.parseXHoleMultiplier();
        this.s2InReels = [data.evaluatedArea[1].indexOf(SYMBOL.S2), data.evaluatedArea[3].indexOf(SYMBOL.S2)];
        this.setStickyStuffs(data);
        this.parseXGodAndXMental();
        this.parseStickyDeadMultiplier(data);
    }

    public isXMentalActive(): boolean {
        let isActive: boolean = false;
        if (this.xMentalPos.x > -1 && this.xMentalPos.y > -1) {
            if (this.data.fireReels[this.xMentalPos.x] || this.data.fireFrames[this.xMentalPos.x][this.xMentalPos.y]) {
                isActive = true;
            }
        }
        return isActive;
    }

    public isXHoleActive(): boolean {
        return this.gameData.reels[2][1] === SYMBOL.X_HOLE && (this.gameData.fireFrames[2][1] || this.gameData.fireReels[2])
    }

    public bonusFinalWinRatio(): number {
        return Game.model.gameData.accumulatedRoundWin / Game.getBuyFeaturePrice();
    }

    public shouldPlayAnticipation(): boolean {
        return !((Game.buyFeatureName === GameFeatureMode.FREESPIN || Game.buyFeatureName === GameFeatureMode.FREESPIN_COOL ||
            Game.buyFeatureName === GameFeatureMode.FREESPIN_SUPER || this.data.mode !== GameFeatureMode.NORMAL))
    }

    public isFSUpgrade(): boolean {
        return this.data.mode === GameFeatureMode.FREESPIN && (this.data.nextMode === GameFeatureMode.FREESPIN_COOL || this.data.nextMode === GameFeatureMode.FREESPIN_SUPER);
    }

    public isCFSUpgrade(): boolean {
        return this.data.mode === GameFeatureMode.FREESPIN_COOL && this.data.nextMode === GameFeatureMode.FREESPIN_SUPER;
    }

    public isCFS(): boolean {
        return Game.model.data.mode === GameFeatureMode.FREESPIN_COOL;
    }

    public isSFS(): boolean {
        return Game.model.data.mode === GameFeatureMode.FREESPIN_SUPER;
    }

    public isStickyDeadApplicable(): boolean {
        return this.isSFS() || this.isCFS();
    }

    protected onInit(data: Mental2InitData): void {
        super.onInit(data);
        this.isRestore = data.isRestoreState;
        this.scatterAnticipationReels = [false, false, false, false, false];
        if (data.nextMode === GameFeatureMode.FREESPIN_SUPER || data.nextMode === GameFeatureMode.FREESPIN_COOL) {
            data.lockedS2inSFS.forEach((s2Data: IStickyScatter) => {
                if (s2Data.x === 3) {
                    this.s2InReels[1] = s2Data.y;
                } else {
                    this.s2InReels[0] = s2Data.y;
                }
            });
        }
        this.setStickyStuffs(data);
        this.parseXGodAndXMental();
    }

    private parseXGodAndXMental(): void {
        this.xGodInReels = [-1, -1, -1, -1, -1];
        this.xMentalPos = new PIXI.Point(-1, -1);
        let isXGodMiss: boolean = false;
        for (let reelId: number = 0; reelId < this.data.reels.length; reelId++) {
            const xGodRow: number = this.data.reels[reelId].indexOf(SYMBOL.GOD);
            const xMentalRow: number = this.data.reels[reelId].indexOf(SYMBOL.X_MENTAL);
            if (xGodRow > -1 && !isXGodMiss) {
                this.xGodInReels[reelId] = xGodRow;
            } else {
                isXGodMiss = true;
            }
            if (xMentalRow > -1) {
                this.xMentalPos.set(reelId, xMentalRow);
            }
        }
    }

    private resetAllMultiplierPlacedPosition(data: Mental2GameData): void {
        this.deadMultiplierPlacedPositions = [];
        this.deadMultiplierPlacedValues = [];
        for (let reel: number = 0; reel < data.finalEvaluatedArea.length; reel++) {
            this.deadMultiplierPlacedPositions[reel] = [];
            this.deadMultiplierPlacedValues[reel] = [];
            for (let symbol: number = 0; symbol < data.finalEvaluatedArea[reel].length; symbol++) {
                this.deadMultiplierPlacedPositions[reel][symbol] = [];
                this.deadMultiplierPlacedValues[reel][symbol] = [];
                for (let k: number = 0; k < data.finalEvaluatedArea[reel][symbol].length; k++) {
                    for (let l = 0; l < data.finalEvaluatedArea[reel][symbol][k].size; l++) {
                        this.deadMultiplierPlacedPositions[reel][symbol].push(false);
                        this.deadMultiplierPlacedValues[reel][symbol].push(1);
                    }

                }
            }
        }
    }

    private onBet(betData: APIFeatureBetEventData): void {
        for (let index: number = 0; index < Game.model.initData.featureBuyTimesBetValue.length; index++) {
            if (Game.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                Game.buyFeatureName = betData.featureName;
                break;
            }
        }
    }

    private onFeatureName(name: string): void {
        Game.buyFeatureName = name;
    }

    private setStickyStuffs(data: Data): void {
        this.stickyS2 = [-1, -1, -1, -1, -1];
        data.lockedS2inSFS.forEach((stickyS2Data: IStickyScatter) => {
            this.stickyS2[stickyS2Data.x] = stickyS2Data.y;
        });
    }

    private parseXHoleMultiplier(): void {
        this.gameData.xholeCoordMap.forEach((xHoleCoordInfo: IXHoleCordInfo) => {
            this.xHoleMultiplier[xHoleCoordInfo.toCoord.x] = this.xHoleMultiplier[xHoleCoordInfo.toCoord.x] || [];
            this.xHoleMultiplier[xHoleCoordInfo.toCoord.x][xHoleCoordInfo.toCoord.y] = xHoleCoordInfo.multiplier;
        });
    }

    private calculateFireFrame(data: Mental2GameData): number {
        let ffCount: number = 0;
        data.fireFrames.forEach((reels: boolean[], reelId: number) => {
            if (data.fireReels[reelId]) {
                ffCount += Config.SYMBOLS_IN_REEL[reelId];
            } else {
                reels.forEach((isFireFrame: boolean) => {
                    if (isFireFrame) {
                        ffCount++;
                    }
                });
            }
        });
        return ffCount;
    }

    private setLastConfig(winRatio: number, configs: IBigWinLevel[]): IBigWinLevel[] {
        let lastConfig: IBigWinLevel = configs[configs.length - 1];
        if (lastConfig.from * 1.2 > winRatio && configs.length > 1) {
            configs.pop();
            return this.setLastConfig(winRatio, configs);
        }
        lastConfig.to = winRatio;
        return configs;
    }

    private calculateWinRatioConfig(ratio: number): IWinRatioConfig {
        for (let index: number = Config.WIN_RATIOS.normalWin.length - 1; index >= 0; index--) {
            if (ratio >= Config.WIN_RATIOS.normalWin[index].ratio) {
                return Config.WIN_RATIOS.normalWin[index];
            }
        }
        return Config.WIN_RATIOS.normalWin[0];
    }

    private parseBetWaysWin(data: Mental2GameData): void {
        const allWinPos: boolean[][][] = [];
        data.betWayWins.forEach((betWayWin: IBetWayWinData) => {
            const reelWinPos: boolean[][][] = [];
            const finalEvalArea: IFinalEvaluatedArea[][][] = this.isXHoleActive() ? data.finalEvaluatedAreaAfterXhole : data.finalEvaluatedArea;
            for (let reelId: number = 0; reelId < finalEvalArea.length; reelId++) {
                reelWinPos.push([]);
                allWinPos[reelId] = allWinPos[reelId] || [];
                for (let rowId: number = 0; rowId < finalEvalArea[reelId].length; rowId++) {
                    allWinPos[reelId][rowId] = allWinPos[reelId][rowId] || [];
                    reelWinPos[reelId][rowId] = [];
                    let symSubIndex: number = 0;
                    for (let splitIndex: number = 0; splitIndex < finalEvalArea[reelId][rowId].length; splitIndex++) {
                        const sym: string = finalEvalArea[reelId][rowId][splitIndex].symTo;
                        for (let sizeIndex: number = 0; sizeIndex < finalEvalArea[reelId][rowId][splitIndex].size; sizeIndex++) {
                            const isWin: boolean = (betWayWin.symbol === sym || this.isWild(sym)) && reelId <= betWayWin.length;
                            reelWinPos[reelId][rowId].push(isWin);
                            allWinPos[reelId][rowId][symSubIndex] = allWinPos[reelId][rowId][symSubIndex] || isWin;
                            symSubIndex++;
                        }
                    }
                }

            }
            this.reelWinPos.set(betWayWin.symbol, reelWinPos);
        });
        data.allWinPos = allWinPos;
    }

    private isWild(sym: string) {
        return sym === SYMBOL.WILD || sym === SYMBOL.X_NUDGE;
    }

    private parseScatterLanded(currentReels: string[][]): number[] {
        let scatterPosInReels: number[] = [-1, -1, -1, -1, -1];
        let reelData: string[][] = JSON.parse(JSON.stringify(currentReels));
        reelData.forEach((reel: string[], reelNo: number) => {
            reel.forEach((symName: string, symNo) => {
                if (symName === "S1" || symName === "S2") {
                    scatterPosInReels[reelNo] = symNo;
                }
            });
        });
        return scatterPosInReels;
    }

    private parseScatterAnticipationReels(): boolean[] {
        let anticipationReel: boolean[] = [false, false, false, false, false];
        if (!this.shouldPlayAnticipation()) {
            return anticipationReel;
        }
        if (this.totalScatterLanded >= 2) {
            if (this.scatterLandedPos[0] > -1 && this.scatterLandedPos[2] > -1) {
                anticipationReel[3] = true;
                anticipationReel[4] = true;
            }
        }
        return anticipationReel;
    }

    public isEligibleForSFSTriggered():boolean{
        return  this.scatterLandedPos[0] > -1 && this.scatterLandedPos[2] > -1 && this.s2InReels[0] > -1 && this.s2InReels[1] > -1;
    }
    private parseTotalScatters(scatterPos: number[]): number {
        let totalScatters: number = 0;
        scatterPos.forEach((item: number) => {
            if (item > -1) {
                totalScatters++;
            }
        });
        return totalScatters;
    }

    get isBigWin(): boolean {
        let isBigWin: boolean = (this.winRatio >= ((this.gameData.wasFeatureBuy) ? (50) : ((Game.buyFeatureName === "BOOSTED_BET_4") ? 1600 : 15)));
        if ((Game.buyFeatureName === "BOOSTED_BET_3" || Game.buyFeatureName === "BOOSTED_BET_4") && Game.isWinBelowStake()) {
            isBigWin = false;
        }
        return isBigWin; // #355
    }
}